import { registerDevice, type DeviceProfile, type DeviceRegistration } from '../api/auth'
import { getKidSession } from './kidSession'

/**
 * The device's identity, and the boot sequence around it —
 * offline-sync.md §8, the build spec
 *
 * 🔴 A MODULE, NOT A REACT CONTEXT. The outbox (§4.6) reads the token from a
 * 30-second timer and a `window.online` handler, neither of which is inside
 * React's lifecycle. A hook-only API would drag the offline queue into the
 * component tree to no benefit; components get a thin `useDevice()` when a
 * screen needs one (Unit 6).
 */

const DEVICE_ID_KEY = 'kalqy.deviceId'
const REGISTRATION_KEY = 'kalqy.device'

/** The states a caller can be in. `ensureDevice()` never rejects. */
export type DeviceState =
  /** Registered: `playerId` and a device token are cached. */
  | { status: 'ready'; deviceId: string; playerId: string; deviceToken: string }
  /**
   * Not registered, and that is a normal state — offline at first launch, or no
   * profile collected yet (OD-40). Games still run; everything queues.
   */
  | { status: 'unregistered'; deviceId: string }

/**
 * Minimal `localStorage` shape, injectable so the tests (and Node) do not need
 * a DOM, and so a browser that throws on access cannot take the boot path down.
 */
export interface DeviceStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function defaultStorage(): DeviceStorage | null {
  try {
    // Absent under Node (where the tests run), and Safari private mode throws on
    // ACCESS rather than on use — so this is a try/catch, not a truthiness check.
    return (globalThis as { localStorage?: DeviceStorage }).localStorage ?? null
  } catch {
    return null
  }
}

let storage: DeviceStorage | null = defaultStorage()
let inFlight: Promise<DeviceState> | null = null
let postRegister: (() => void | Promise<void>) | null = null

/** Test seam. Also the escape hatch if a browser's storage throws. */
export function setDeviceStorage(next: DeviceStorage | null): void {
  storage = next
}

function read(key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    storage?.setItem(key, value)
  } catch {
    /*
     * Non-fatal on purpose. A device that cannot persist still plays — it
     * re-mints on the next boot, which costs that device's history and not the
     * child's session. Failing the boot here would trade a data problem for a
     * blank screen in front of a four-year-old.
     */
  }
}

/**
 * 🔴 MINTED ONCE, AND NEVER RE-MINTED ON FAILURE.
 *
 * The id *is* the identity: `POST /auth/device` is idempotent on it, so the
 * same id returns the same `Player`. Re-minting after a failed registration
 * would make the next successful one **a different child**, and the first
 * child's queued events would be attributed to a player that no longer has a
 * device.
 *
 * `crypto.randomUUID()` because §4.1's DTO enforces a v4 UUID server-side: the
 * id is a bearer credential (public + idempotent endpoint), so a guessable one
 * would turn a bounded risk into enumeration of a whole class.
 */
export function getDeviceId(): string {
  const existing = read(DEVICE_ID_KEY)
  if (existing) return existing

  const minted = crypto.randomUUID()
  write(DEVICE_ID_KEY, minted)
  return minted
}

function readRegistration(): DeviceRegistration | null {
  const raw = read(REGISTRATION_KEY)
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { playerId, deviceToken } = parsed as Partial<DeviceRegistration>
    /*
     * 🔴 BOTH OR NEITHER. A half-cache is a device that believes it is
     * registered and cannot write: every event would 401 and — being a 4xx —
     * be discarded by the outbox rather than retried.
     */
    if (typeof playerId !== 'string' || playerId.length === 0) return null
    if (typeof deviceToken !== 'string' || deviceToken.length === 0) return null
    return { playerId, deviceToken }
  } catch {
    return null
  }
}

/** The current state, without touching the network. */
export function getDeviceState(): DeviceState {
  const deviceId = getDeviceId()
  const registration = readRegistration()
  return registration
    ? { status: 'ready', deviceId, ...registration }
    : { status: 'unregistered', deviceId }
}

/**
 * `bearerFor('device')` — the token, or null when this device has never
 * registered. §4.6 calls this **at flush time**, never at enqueue time: an item
 * queued at first launch is written with a token that did not exist when the
 * child played.
 */
export function getDeviceToken(): string | null {
  /*
   * 🔴 OD-52 — A SIGNED-IN CHILD'S TOKEN WINS OVER THE TABLET'S.
   *
   * `getKidSession()` is set by `POST /auth/kid` and names the roster row the
   * child proved they are; `readRegistration()` names whatever blank player this
   * tablet minted the first time it booted. Both are device tokens with the same
   * shape and the same audience — the difference is WHOSE.
   *
   * ⚠️ THE PRECEDENCE HAS TO LIVE HERE, NOT IN A SCREEN, because §4.6 calls
   * this **at flush time** from a 30-second timer and a `window.online`
   * handler. A screen that "passed the right token down" would still leave the
   * outbox draining a morning of one child's rounds under another child's
   * credential — silently, and only on the offline path.
   *
   * ⚠️ AND IT FALLS BACK RATHER THAN REPLACING. A school that has not run a
   * roster import has no class code and no passwords; `ensureDevice` is still
   * that school's whole boot path, and it must keep working untouched.
   */
  return getKidSession()?.deviceToken ?? readRegistration()?.deviceToken ?? null
}

/**
 * The `playerId`, or null. §4.6 needs it at flush time too — `POST
 * /progress/:playerId` puts it in the **path**, which cannot be built before
 * registration, so a queued progress item must store a path *template*.
 */
export function getPlayerId(): string | null {
  /*
   * 🔴 THE SAME PRECEDENCE AS `getDeviceToken`, AND THE TWO MUST NEVER DISAGREE.
   * §4.6 builds `POST /progress/:playerId` from this at flush time and signs it
   * with that; a token naming the signed-in child and a path naming the tablet's
   * blank player would be refused by `OwnPlayerGuard` on every single item — a
   * queue that never drains and never says why.
   */
  return getKidSession()?.playerId ?? readRegistration()?.playerId ?? null
}

/**
 * §4.6 registers the outbox drain here.
 *
 * 🔴 A CALLBACK, NOT AN IMPORT. offline-sync.md §8 gives the order — *"register
 * the device (idempotent), then **drain the outbox** before anything else"* —
 * and this slice owns the *moment*, not the queue. A direct import would point
 * the dependency the wrong way and force §4.5's tests to stand up an IndexedDB.
 */
export function setPostRegisterHook(hook: (() => void | Promise<void>) | null): void {
  postRegister = hook
}

/**
 * Boot: return the cached registration, or register once, then drain.
 *
 * 🔴 NEVER REJECTS, AND NEVER BLOCKS PLAY. *"Gameplay never awaits the
 * network"* (offline-sync.md §1) and *"offline at first launch → games still
 * run, everything queues"* (§8). Anything that can throw on the boot path of a
 * children's game is a white screen for a four-year-old.
 *
 * 🔴 AND IT DOES NOT REGISTER WITHOUT A PROFILE — see OD-40. `name` and
 * `ageBand` are required by §4.1's DTO, nothing collects them before Unit 6's
 * screens, and the tempting placeholder is **permanent**: registration is
 * deliberately non-mutating, so a class of twenty tablets would produce twenty
 * children called "Child" on a teacher's dashboard with no way to correct them
 * from the device.
 */
export function ensureDevice(profile?: DeviceProfile): Promise<DeviceState> {
  const current = getDeviceState()
  if (current.status === 'ready') return Promise.resolve(current)

  // No profile: a legitimate resting state, and no request is made.
  if (!profile) return Promise.resolve(current)

  /*
   * 🔴 ONE REGISTRATION PER BOOT, WHATEVER ASKS.
   *
   * React 19 StrictMode invokes effects twice in development, and the shell and
   * the outbox both want the device. §4.1 built the concurrent-registration
   * race path server-side because this happens — but leaning on it burns a slot
   * of a 10/min limit that a classroom of twenty devices shares, and mints a
   * token nobody keeps.
   */
  inFlight ??= registerOnce(current.deviceId, profile).finally(() => {
    inFlight = null
  })

  return inFlight
}

async function registerOnce(deviceId: string, profile: DeviceProfile): Promise<DeviceState> {
  let registration: DeviceRegistration
  try {
    registration = await registerDevice(deviceId, profile)
  } catch {
    /*
     * 🔴 SILENT. Invariant I6: the child never sees a network state — not a
     * toast, not a spinner, not a retry button. They are four; it is not their
     * problem, and a scary icon mid-session ends the session. The adult-facing
     * chip is §4.7's job, in the teacher chrome.
     *
     * The deviceId is untouched, so the next attempt is the same child.
     */
    return { status: 'unregistered', deviceId }
  }

  // Written as one value: a half-cache is worse than no cache (see above).
  write(REGISTRATION_KEY, JSON.stringify(registration))

  /*
   * THEN the drain (offline-sync.md §8). This ordering is the whole point of
   * the hook: the common case is a device used offline yesterday that is back
   * on the school network now, and its queue cannot flush until there is a
   * token to flush with.
   */
  try {
    await postRegister?.()
  } catch {
    // A failing drain must not un-register the device or fail the boot. The
    // outbox owns its own retries (§4.6).
  }

  return { status: 'ready', deviceId, ...registration }
}

/** Test seam: clears the module-level in-flight promise and the hook. */
export function resetDeviceRuntime(): void {
  inFlight = null
  postRegister = null
}
